import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";

const IssuesTab = ({ onCountChange }) => {
  const [data, setData] = useState({ failed_payments: [], failed_emails: [], counts: { payments: 0, emails: 0, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchIssues = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/failed-attempts`, authHeaders());
      setData(res.data);
      if (onCountChange) onCountChange(res.data.counts?.total || 0);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    fetchIssues();
    const id = setInterval(fetchIssues, 60000); // poll every 60s
    return () => clearInterval(id);
  }, [fetchIssues]);

  const dismissPayment = async (sessionId) => {
    setBusyId(sessionId);
    try {
      await axios.post(`${API}/admin/payment-failures/${encodeURIComponent(sessionId)}/dismiss`, {}, authHeaders());
      toast.success("Dismissed");
      fetchIssues();
    } catch (e) {
      toast.error("Could not dismiss");
    } finally {
      setBusyId(null);
    }
  };

  const dismissEmail = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/email-failures/${id}/dismiss`, {}, authHeaders());
      toast.success("Dismissed");
      fetchIssues();
    } catch (e) {
      toast.error("Could not dismiss");
    } finally {
      setBusyId(null);
    }
  };

  const retryEmail = async (id) => {
    setBusyId(id);
    try {
      const res = await axios.post(`${API}/admin/email-failures/${id}/retry`, {}, authHeaders());
      toast.success(`Email resent (Resend ID: ${res.data.id || "ok"})`);
      fetchIssues();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-brand-text-muted font-sans text-sm py-8">
        <Loader2 size={16} className="animate-spin" /> Loading issues...
      </div>
    );
  }

  const total = data.counts?.total || 0;

  return (
    <div className="space-y-6" data-testid="issues-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl text-brand-text flex items-center gap-2">
            <AlertTriangle size={20} className="text-brand-gold" /> Issues & Failed Attempts
          </h2>
          <p className="font-sans text-xs text-brand-text-muted mt-1">
            {total === 0
              ? "All clear — no failed deposits or email failures."
              : `${total} item${total === 1 ? "" : "s"} need your attention.`}
          </p>
        </div>
        <button
          onClick={fetchIssues}
          data-testid="issues-refresh"
          className="font-sans text-xs text-brand-gold border border-brand-gold/40 px-3 py-1 hover:bg-brand-gold/10 transition-colors flex items-center gap-1.5"
        >
          <RotateCcw size={12} /> Refresh
        </button>
      </div>

      {/* ===== Failed Deposits ===== */}
      <div className="bg-brand-surface border border-brand-border">
        <div className="px-4 py-3 border-b border-brand-border flex items-center justify-between">
          <h3 className="font-serif text-base text-brand-text">
            🟠 Deposits that didn't go through
          </h3>
          <span className="font-sans text-xs text-brand-text-muted">{data.failed_payments.length}</span>
        </div>
        {data.failed_payments.length === 0 ? (
          <p className="font-sans text-xs text-brand-text-muted p-4 text-center">No failed deposit attempts. 🎉</p>
        ) : (
          <div className="divide-y divide-brand-border">
            {data.failed_payments.map((p) => (
              <div key={p.session_id} className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid={`failed-payment-${p.session_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-sans text-sm text-brand-text font-semibold">{p.client_name || "Unknown client"}</span>
                    <span className="font-sans text-[10px] uppercase tracking-wider bg-orange-500/15 text-orange-300 border border-orange-500/40 px-1.5 py-0.5">
                      {p.payment_status}
                    </span>
                    {p.booking_status === "confirmed" && (
                      <span className="font-sans text-[10px] uppercase tracking-wider bg-green-500/15 text-green-300 border border-green-500/40 px-1.5 py-0.5">
                        Booking confirmed
                      </span>
                    )}
                  </div>
                  <p className="font-sans text-xs text-brand-text-muted mt-1">
                    <span className="text-brand-gold">{p.service_name || "—"}</span>
                    {p.date && (<> · {p.date} {p.time}</>)}
                  </p>
                  <p className="font-sans text-xs text-brand-text-muted mt-0.5">
                    {p.client_phone && (<>📞 {p.client_phone}</>)} {p.client_email && (<> · ✉️ {p.client_email}</>)}
                  </p>
                  <p className="font-sans text-[11px] text-orange-300/80 mt-1">Reason: {p.failure_reason}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {p.client_phone && (
                    <a href={`tel:${p.client_phone}`} data-testid={`call-${p.session_id}`} className="font-sans text-xs text-brand-gold border border-brand-gold/40 px-3 py-1.5 hover:bg-brand-gold/10 transition-colors">
                      Call
                    </a>
                  )}
                  {p.client_phone && (
                    <a href={`sms:${p.client_phone}`} className="font-sans text-xs text-brand-text border border-brand-border px-3 py-1.5 hover:border-brand-gold hover:text-brand-gold transition-colors">
                      Text
                    </a>
                  )}
                  <button
                    onClick={() => dismissPayment(p.session_id)}
                    disabled={busyId === p.session_id}
                    data-testid={`dismiss-payment-${p.session_id}`}
                    className="font-sans text-xs text-brand-text-muted border border-brand-border px-3 py-1.5 hover:text-red-300 hover:border-red-300 transition-colors disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== Failed Emails ===== */}
      <div className="bg-brand-surface border border-brand-border">
        <div className="px-4 py-3 border-b border-brand-border flex items-center justify-between">
          <h3 className="font-serif text-base text-brand-text">
            🔴 Emails that did not send
          </h3>
          <span className="font-sans text-xs text-brand-text-muted">{data.failed_emails.length}</span>
        </div>
        {data.failed_emails.length === 0 ? (
          <p className="font-sans text-xs text-brand-text-muted p-4 text-center">No failed emails. 📬</p>
        ) : (
          <div className="divide-y divide-brand-border">
            {data.failed_emails.map((em) => (
              <div key={em.id} className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid={`failed-email-${em.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-sans text-sm text-brand-text font-semibold truncate">{em.recipient || "—"}</span>
                    <span className="font-sans text-[10px] uppercase tracking-wider bg-red-500/15 text-red-300 border border-red-500/40 px-1.5 py-0.5">
                      {em.kind}
                    </span>
                  </div>
                  <p className="font-sans text-xs text-brand-text-muted mt-1 truncate">{em.subject || "(no subject)"}</p>
                  <p className="font-sans text-[11px] text-red-300/80 mt-1">{em.error}</p>
                  {em.created_at && (
                    <p className="font-sans text-[10px] text-brand-text-muted/70 mt-0.5">{new Date(em.created_at).toLocaleString()}</p>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => retryEmail(em.id)}
                    disabled={busyId === em.id}
                    data-testid={`retry-email-${em.id}`}
                    className="font-sans text-xs text-brand-gold border border-brand-gold/40 px-3 py-1.5 hover:bg-brand-gold/10 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {busyId === em.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Retry
                  </button>
                  <button
                    onClick={() => dismissEmail(em.id)}
                    disabled={busyId === em.id}
                    data-testid={`dismiss-email-${em.id}`}
                    className="font-sans text-xs text-brand-text-muted border border-brand-border px-3 py-1.5 hover:text-red-300 hover:border-red-300 transition-colors disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default IssuesTab;
